import {FilterInput, FilterWrapper, RenderFilterProps, useTableData} from "../../../../misc/scroller/Table";
import React, {useContext} from "react";
import {EditContext} from "../../../../misc/scroller/EditProvider";
import {CheckboxEl} from "../ShopTableCol";
import {QuoteOpening} from "../../../../api/QuoteOpenings";
import {Opening} from "../../../../api/Openings";
import {CircularProgress, Tooltip, useTheme} from "@mui/material";
import {HelpOutline} from "@mui/icons-material";
import {deepOrange} from "@mui/material/colors";
import {DropDownContext, useDropdownData, ValueItem} from "../../settings/DropDowns";
import {DoorElevationOption} from "../../../../api/ElevationTool";

export function renderDoorElevationStr(data: {
    doorElevation: string;
    inactiveDoorElevation: string;
}, stripThermal: boolean = false) {
    let str = stripDoorElevationPrefix(data.doorElevation || "", stripThermal);
    if(data.inactiveDoorElevation !== "" && data.inactiveDoorElevation !== data.doorElevation) {
        str = stripDoorElevationPrefix(data.doorElevation, stripThermal) + ", " + stripDoorElevationPrefix(data.inactiveDoorElevation, stripThermal);
    }

    return str;
}

export const thermalPrefix = "Thermal ";

// marks a file-based door elevation in edit views, so it reads apart from the drw-based one of
// the same name. Customer-facing output does not show it.
export const legacyDoorElevationSuffix = " (v1)";

const legacyPickerPrefix = "v1:";

// the drw door elevation picker's options: the scope's and the global drw elevations by id, then
// the file-based (v1) names.
export function doorElevationPickerOptions(list: DoorElevationOption[]) {
    return list.map(o => o.legacy
        ? {value: legacyPickerPrefix + o.name, display: o.name + legacyDoorElevationSuffix}
        : {value: o.id.toString(), display: o.name});
}

// the file-based door elevation name of a picker value, or null for a drw elevation.
export function legacyPickerName(value: string): string | null {
    return value.startsWith(legacyPickerPrefix) ? value.substring(legacyPickerPrefix.length) : null;
}

// the opening's legacy door elevation columns, set to a file-based name. The server finds the
// file other openings use for the name.
export function withLegacyDoorElevation<T extends Opening | QuoteOpening>(row: T, name: string): T {
    return {
        ...row,
        doorElevation: name,
        doorElevationFile: undefined,
        inactiveDoorElevation: "",
        inactiveDoorElevationFile: undefined,
    };
}

// true when the opening uses a file-based door elevation (no drw door link).
export function isLegacyDoorElevation(data: {doorElevation: string; drwDoorElevation?: number | null}) {
    return !data.drwDoorElevation && !!data.doorElevation;
}

export function hasThermalPrefix(value: string) {
    return value.startsWith(thermalPrefix);
}

export function addThermalPrefix(value: string, ok: boolean) {
    if(!ok) return value;
    if(value === "") return value;
    if(hasThermalPrefix(value)) return value;

    return thermalPrefix + value;
}

export function stripDoorElevationPrefix(value: string, ok: boolean) {
    if(!ok) return value;

    if(value.startsWith(thermalPrefix)) {
        return value.substring(thermalPrefix.length)
    }

    return value;
}

export function DoorElevationFilter(props: RenderFilterProps) {
    const editCtx = useContext(EditContext)
    const {value, onChange} = props;
    const hasThermal = hasThermalPrefix(value);

    return (
        <FilterWrapper style={{position: "relative"}}>
            <FilterInput
                type="text" value={stripDoorElevationPrefix(value, true)} placeholder="Filter"
                onFocus={e => {
                    editCtx.onSelect.emit(null)
                }}
                onChange={e => onChange(addThermalPrefix(e.target.value, hasThermal))}
            />

            <CheckboxEl
                style={{position: "absolute", top: 1, right: 6, width: 20, zIndex: 2}}
                checked={hasThermalPrefix(value)}
                onChange={checked => {
                    if(checked) {
                        onChange(thermalPrefix + value)
                    } else {
                        onChange(stripDoorElevationPrefix(value, true))
                    }
                }}
            />
        </FilterWrapper>
    )
}

export function canHaveThermal(elevation: string, opts: ValueItem[]) {
    if(elevation === "") return false;

    const elev = opts.find(e => e.value === elevation);
    if(elev === undefined) return true // custom can have it

    if("canHaveThermal" in elev && elev.canHaveThermal) {
        return true;
    }

    return false;
}

export function DoorElevationCell(props: {
    data: Opening | QuoteOpening
}) {
    const {data} = props;
    const ddlCtx = useContext(DropDownContext);
    const opts = useDropdownData(ddlCtx.doorElevations);

    if(ddlCtx.loading) {
        return <CircularProgress />
    }

    return (
        <div style={{display: "flex", position: "relative"}}>
            <div style={{flex: 1}}>
                {renderDoorElevationStr(data, true)}
                {isLegacyDoorElevation(data) && legacyDoorElevationSuffix}
            </div>

            <CheckboxEl
                style={{position: "absolute", top: 0, right: 2, width: 20, zIndex: 2}}
                checked={hasThermalPrefix(data.doorElevation)}
                disabled={!canHaveThermal(data.doorElevation, opts)}
                onChange={checked => {
                    const d = Object.assign({}, data)

                    if(checked) {
                        d.doorElevation = thermalPrefix + d.doorElevation;

                        if(d.inactiveDoorElevation !== "") {
                            d.inactiveDoorElevation = thermalPrefix + d.inactiveDoorElevation;
                        }
                    } else {
                        d.doorElevation = stripDoorElevationPrefix(d.doorElevation, true)
                        if(d.inactiveDoorElevation !== "") {
                            d.inactiveDoorElevation = stripDoorElevationPrefix(d.inactiveDoorElevation, true)
                        }
                    }

                    return d;
                }}
            />
        </div>
    )
}

export function DoorElevationColumnHeader() {
    const editCtx = useContext(EditContext);
    const data = useTableData<Opening|QuoteOpening>(editCtx.tableName)

    const missingElev = data.filter(d => {
        if(d.doorElevation !== "" && !d.doorElevationFile) return true;
        if(d.inactiveDoorElevation !== "" && !d.inactiveDoorElevationFile) return true;
        return false;
    }).length;

    return (
        <div style={{display: "flex", width: "100%"}}>
            <div style={{flex: 1}}>Door Elevation</div>
            <CountBadge count={missingElev} label="Missing attachments" />
            <Tooltip title="Thermal Kit">
                <div style={{fontSize: "0.9rem"}}>
                    <HelpOutline fontSize="inherit"/>
                </div>
            </Tooltip>
        </div>
    )
}

export function CountBadge(props: {
    count: number;
    label: string;
}) {
    const theme = useTheme();

    if(props.count <= 0) return null;

    return (
        <Tooltip title={props.label}>
            <div style={{
                display: "inline-block",
                alignSelf: "center",
                paddingLeft: 6, paddingRight: 6,
                paddingTop: 2, paddingBottom: 2,
                backgroundColor: deepOrange["900"], color: theme.palette.primary.contrastText,
                borderRadius: 8,
                fontSize: "0.7rem", fontWeight: "600",
                marginLeft: 4, marginRight: 4,
            }}>
                {props.count}
            </div>
        </Tooltip>
    )
}